import { isEmpty } from 'lodash';
import { CSSProperties, useMemo } from 'react';
import { TextSize } from '@shared/components/Text';
import { ViewProps } from '@shared/components/View';
import { validateUrl } from '@shared/util/validate/url';
import { TruncateMiddle } from './TruncateMiddle';

interface Props extends ViewProps {
  href: string;
  showProtocol?: boolean;
  preserve?: number;
  textSize?: TextSize;
  style?: CSSProperties;
}

export function TruncateLink({
  href,
  showProtocol = false,
  preserve = 8,
  textSize,
  ...otherProps
}: Props) {
  if (validateUrl(href)) {
    return (
      <TruncateUrl
        href={href}
        showProtocol={showProtocol}
        preserve={preserve}
        textSize={textSize}
        {...otherProps}
      />
    );
  } else {
    return <TruncateHref href={href} textSize={textSize} {...otherProps} />;
  }
}

function TruncateUrl({ href, showProtocol = false, preserve = 8, textSize, ...otherProps }: Props) {
  const url = useMemo(() => new URL(href), [href]);

  // truncate in the middle, preserving last N characters of pathname
  const { origin, hostname, pathname } = url;
  const host = showProtocol ? origin : hostname;
  const path = pathname.replace(/^\//, '');
  const pathEnd = path.slice(-preserve);
  const pathBeginning = path.slice(0, -preserve);
  const truncatedString = [host, !isEmpty(path) && '/', pathBeginning].filter(Boolean).join('');

  return (
    <TruncateMiddle beginning={truncatedString} end={pathEnd} textSize={textSize} {...otherProps} />
  );
}

export function TruncateHref({
  href,
  textSize,
  ...otherProps
}: Omit<Props, 'preserve' | 'showProtocol'>) {
  return <TruncateMiddle beginning={href} textSize={textSize} {...otherProps} />;
}
